import { DataGrid, ScrollView, TextBox } from "devextreme-react";
import Button from "devextreme-react/button";
import {
  Column,
  Item as DataGridItem,
  DataGridRef,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import Form, {
  EmailRule,
  Item as FormItem,
  FormRef,
  Label,
  RequiredRule,
  StringLengthRule,
} from "devextreme-react/form";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import CustomStore from "devextreme/data/custom_store";
import notify from "../../lib/notify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAccount } from "../../api/auth";
import { fetchManagers, fetchRoles } from "../../api/users";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import axios from "../../lib/axios";
import { RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./EmployeesList.scss";

const companyName = (id: number | string | null | undefined): string => {
  if (id == null) return "";
  switch (Number(id)) {
    case 1:
      return "Invenio";
    case 2:
      return "Invenio QD";
    case 3:
      return "Quality & Human Services";
    case 4:
      return "IPSUPPORT";
    default:
      return "";
  }
};

const roleName = (id: number | string | null | undefined): string => {
  if (id == null) return "";
  switch (Number(id)) {
    case 1:
      return "Administrator główny";
    case 2:
      return "Administrator spółki";
    case 3:
      return "Kadry spółki";
    case 4:
      return "Manager";
    case 5:
      return "Kierownik";
    case 6:
      return "Pracownik";
    case 7:
      return "Majster";
    default:
      return "";
  }
};

const statusColor = (employee: any): string => {
  const isRegistered = employee?.isRegistered;
  const isConfirmed = employee?.emailConfirmed;
  const isDeactivated = employee?.isDeactivated;
  if (isDeactivated) return "#facc15";
  if (!isConfirmed) return isRegistered ? "#0000ff" : "#ff0000";
  if (!isRegistered) return "#ff0000";
  return "#008000";
};

function generatePassword(): string {
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const specialChars = "!#@$%^&*()";
  const numbers = "0123456789";
  const allChars = "abcdefghijklmnopqrstuvwxyz";
  const length = 8;
  const randomUppercase = uppercaseChars.charAt(
    Math.floor(Math.random() * uppercaseChars.length),
  );
  const randomSpecial = specialChars.charAt(
    Math.floor(Math.random() * specialChars.length),
  );
  const randomnumber = numbers.charAt(
    Math.floor(Math.random() * numbers.length),
  );
  const remainingLength = length - 2;
  const remainingChars = Array.from({ length: remainingLength }, () =>
    allChars.charAt(Math.floor(Math.random() * allChars.length)),
  ).join("");

  const passwordArray = [
    randomUppercase,
    randomSpecial,
    randomnumber,
    ...remainingChars.split(""),
  ];
  passwordArray.sort(() => Math.random() - 0.5);

  return passwordArray.join("");
}

export default function EmployeesList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const dataGridRef = useRef<DataGridRef>(null);
  const formRef = useRef<FormRef | null>(null);

  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [managers, setManagers] = useState<
    { id: number; firstName: string; lastName: string; companyId: number }[]
  >([]);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(RoleEnum.Pracownik);
  const [selectedManagerCompanyId, setSelectedManagerCompanyId] =
    useState<number>();
  const [selectedManagerId, setSelectedManagerId] = useState<number>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [contractType, setContractType] = useState<number>();
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [filter, setFilter] = useState("");
  const [mobileEmployees, setMobileEmployees] = useState<any[]>([]);
  const [mobileReload, setMobileReload] = useState(0);

  const store = useMemo(() => {
    return new CustomStore({
      key: ["companyId", "id"],
      async load() {
        const effectiveCompanyId = companyId || user?.companyId || 0;
        const { data } = await axios.get(
          `/api/employees?companyId=${effectiveCompanyId}`,
        );

        return data;
      },
    });
  }, [companyId, user?.companyId]);

  useEffect(() => {
    if (!isMobile) return;
    let cancelled = false;
    (async () => {
      try {
        const effectiveCompanyId = companyId || user?.companyId || 0;
        const { data } = await axios.get(
          `/api/employees?companyId=${effectiveCompanyId}`,
        );
        if (!cancelled) setMobileEmployees(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setMobileEmployees([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, user?.companyId, isMobile, mobileReload]);

  const filteredEmployees = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return mobileEmployees;
    return mobileEmployees.filter((e: any) => {
      const fullName = `${e.lastName ?? ""} ${e.firstName ?? ""}`
        .trim()
        .toLowerCase();
      const code = (e.code ?? "").toString().toLowerCase();
      const email = (e.emailAddress ?? "").toLowerCase();
      return fullName.includes(q) || code.includes(q) || email.includes(q);
    });
  }, [mobileEmployees, filter]);

  const togglePopup = useCallback(
    (employee: any = null) => {
      setIsPopupVisible(!isPopupVisible);
      if (employee) {
        setSelectedEmployee(employee);
        setSelectedRole(RoleEnum.Pracownik);
        setContractType(employee.contractType);
        setEmail(employee.emailAddress || "");
        setPassword("");
      }
    },
    [isPopupVisible],
  );

  const downloadRoles = useCallback(async () => {
    try {
      const resp = await fetchRoles();

      if (resp?.isOk && Array.isArray(resp.data)) {
        setRoles(resp.data);
        return;
      }

      const list = Array.isArray(resp?.data?.data)
        ? resp!.data.data
        : Array.isArray(resp?.data)
          ? resp!.data
          : [];

      setRoles(list);
      if (!resp?.isOk) {
        notify(`${t("Error:")} ${resp?.error || t("Unknown error")}`, "error");
      }
    } catch (e: any) {
      setRoles([]);
      notify(`${t("Error:")} ${e?.message || t("Unknown error")}`, "error");
    }
  }, [t]);

  const downloadManagers = useCallback(async () => {
    try {
      const effectiveCompanyId = companyId || user?.companyId || 0;
      if (effectiveCompanyId < 1 || effectiveCompanyId > 4) {
        setManagers([]);
        return;
      }

      const resp = await fetchManagers(effectiveCompanyId);

      if (resp?.isOk && Array.isArray(resp.data)) {
        setManagers(resp.data);
        return;
      }

      const list = Array.isArray(resp?.data?.data)
        ? resp!.data.data
        : Array.isArray(resp?.data)
          ? resp!.data
          : [];

      setManagers(list);
      if (!resp?.isOk) {
        notify(`${t("Error:")} ${resp?.error || t("Unknown error")}`, "error");
      }
    } catch (e: any) {
      setManagers([]);
      notify(`${t("Error:")} ${e?.message || t("Unknown error")}`, "error");
    }
  }, [companyId, t, user?.companyId]);

  const handleCreateAccount = useCallback(async () => {
    const validationResult = formRef.current?.instance().validate();
    if (validationResult?.isValid) {
      setIsFormDisabled(true);

      try {
        const { companyId, id: employeeId } = selectedEmployee;

        const message = await createAccount(
          companyId,
          employeeId,
          contractType || 0,
          selectedRole,
          email,
          password,
          selectedManagerId,
          selectedManagerCompanyId,
        );

        if (message) {
          notify(message, "success", 5000);
        } else {
          notify(t("Account created sucessfull"), "success", 5000);
        }

        // Reset formularza
        setIsPopupVisible(false);
        setSelectedEmployee(null);
        setSelectedRole(RoleEnum.Pracownik);
        setSelectedManagerCompanyId(undefined);
        setSelectedManagerId(undefined);
        setEmail("");
        setPassword("");
        setContractType(undefined);

        downloadManagers();
        dataGridRef.current?.instance().refresh();
        setMobileReload((c) => c + 1);
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
          "error",
          5000,
        );
      } finally {
        setIsFormDisabled(false);
      }
    }
  }, [
    contractType,
    downloadManagers,
    email,
    password,
    selectedEmployee,
    selectedManagerCompanyId,
    selectedManagerId,
    selectedRole,
    t,
  ]);

  useEffect(() => {
    downloadRoles();
  }, [downloadRoles]);

  useEffect(() => {
    downloadManagers();
  }, [companyId, downloadManagers]);

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("employeeList.employeeList")}
        </div>
        <div className="mb-1 text-base">{t("employeeList.headerText")}</div>
        {isMobile ? (
          <div className="employees-mobile">
            <div className="employees-mobile-filter">
              <TextBox
                value={filter}
                onValueChanged={(e) => setFilter(e.value ?? "")}
                placeholder={t("searchPlaceholder")}
                showClearButton
              />
            </div>
            <div className="employees-mobile-list">
              {filteredEmployees.length === 0 ? (
                <div className="employees-mobile-empty">{t("No data")}</div>
              ) : (
                filteredEmployees.map((emp: any) => {
                  const fullName = `${emp.lastName ?? ""} ${
                    emp.firstName ?? ""
                  }`.trim();
                  return (
                    <div
                      key={`${emp.companyId}-${emp.id}`}
                      className="employees-mobile-card"
                    >
                      <div className="employees-mobile-card-status">
                        <span
                          className="employees-mobile-card-status-dot"
                          style={{ backgroundColor: statusColor(emp) }}
                        />
                      </div>
                      <div className="employees-mobile-card-main">
                        <div className="employees-mobile-card-name">
                          {fullName || "—"}
                        </div>
                        {emp.code && (
                          <div className="employees-mobile-card-line">
                            <b>{t("employeeList.acronym")}:</b> {emp.code}
                          </div>
                        )}
                        <div className="employees-mobile-card-line">
                          <b>{t("company")}:</b> {companyName(emp.companyId)}
                        </div>
                        {emp.position && (
                          <div className="employees-mobile-card-line">
                            <b>{t("employeeList.position")}:</b> {emp.position}
                          </div>
                        )}
                        {emp.emailAddress && (
                          <div className="employees-mobile-card-line">
                            <b>{t("employeeList.email")}:</b> {emp.emailAddress}
                          </div>
                        )}
                        {emp.phoneNumber && (
                          <div className="employees-mobile-card-line">
                            <b>{t("employeeList.phoneNumber")}:</b>{" "}
                            {emp.phoneNumber}
                          </div>
                        )}
                        {emp.roleId != null && (
                          <div className="employees-mobile-card-line">
                            <b>{t("employeeList.accountType")}:</b>{" "}
                            {roleName(emp.roleId)}
                          </div>
                        )}
                      </div>
                      <div className="employees-mobile-card-actions">
                        {!emp.isRegistered && (
                          <Button
                            icon="plus"
                            stylingMode="text"
                            hint={t("employeeList.addAcount")}
                            onClick={() => togglePopup(emp)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <DataGrid
            ref={dataGridRef}
            dataSource={store}
            showBorders
            allowColumnResizing
            allowColumnReordering
            searchPanel={{
              visible: true,
              placeholder: t("searchPlaceholder"),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <DataGridItem
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
            </Toolbar>
            <FilterRow visible />
            <Column dataField="isRegistered" visible={false} sortOrder="asc" />
            <Column
              dataField="companyId"
              dataType="string"
              caption={t("company")}
              customizeText={({ value }) => {
                switch (value) {
                  case 1:
                    return "Invenio";
                  case 2:
                    return "Invenio QD";
                  case 3:
                    return "Quality & Human Services";
                  case 4:
                    return "IPSUPPORT";
                  default:
                    return "";
                }
              }}
              minWidth={150}
            />
            <Column
              dataField="code"
              dataType="string"
              caption={t("employeeList.acronym")}
              minWidth={150}
            />
            <Column
              dataField="lastName"
              dataType="string"
              caption={t("employeeList.lastName")}
              minWidth={200}
            />
            <Column
              dataField="firstName"
              dataType="string"
              caption={t("employeeList.firstName")}
              minWidth={200}
            />
            <Column
              dataField="emailAddress"
              dataType="string"
              caption={t("employeeList.email")}
              minWidth={250}
            />
            <Column
              dataField="phoneNumber"
              dataType="string"
              caption={t("employeeList.phoneNumber")}
              minWidth={150}
            />
            <Column
              dataField="position"
              dataType="string"
              caption={t("employeeList.position")}
              minWidth={150}
            />
            <Column
              dataField="roleId"
              caption={t("employeeList.accountType")}
              dataType="number"
              lookup={{
                dataSource: roles,
                valueExpr: "id",
                displayExpr: "name",
                allowClearing: true,
                calculateCellValue: (cell) => {
                  switch (cell) {
                    case 1:
                      return "Administrator główny";
                    case 2:
                      return "Administrator spółki";
                    case 3:
                      return "Kadry spółki";
                    case 4:
                      return "Manager";
                    case 5:
                      return "Kierownik";
                    case 6:
                      return "Pracownik";
                    case 7:
                      return "Majster";
                    default:
                      return "";
                  }
                },
              }}
              minWidth={150}
            />
            <Column
              caption={t("employeeList.status")}
              alignment="center"
              cellRender={(cellData) => {
                const isRegistered = cellData.data?.isRegistered;
                const isConfirmed = cellData.data?.emailConfirmed;
                const isDeactivated = cellData.data?.isDeactivated;

                let iconColor = "#008000";
                if (isDeactivated) {
                  iconColor = "#facc15";
                } else if (!isConfirmed) {
                  iconColor = isRegistered ? "#0000ff" : "#ff0000";
                } else if (!isRegistered) {
                  iconColor = "#ff0000";
                }

                return (
                  <i
                    className="dx-icon dx-icon-indeterminatestate"
                    style={{ color: iconColor }}
                  />
                );
              }}
              minWidth={75}
            />
            <Column
              type="buttons"
              alignment="right"
              buttons={[
                {
                  icon: "plus",
                  onClick: (e) => {
                    if (e.row && !e.row.data.isRegistered) {
                      togglePopup(e.row.data);
                    }
                  },
                  visible: (event) =>
                    !event.row?.data.isRegistered ? true : false,
                  hint: t("employeeList.addAcount"),
                },
              ]}
            />
          </DataGrid>
        )}
        {isPopupVisible ? (
          <Popup
            wrapperAttr={{ class: "employees-popup" }}
            visible
            onHiding={() => togglePopup(null)}
            title={t("employeeList.registerAccountForEmployee")}
            showTitle
            fullScreen={isMobile}
            maxWidth={isMobile ? undefined : 450}
            height={isMobile ? "100%" : "auto"}
            maxHeight={isMobile ? undefined : "80vh"}
            showCloseButton
            dragEnabled={!isMobile}
          >
            <Form ref={formRef} disabled={isFormDisabled}>
              <FormItem
                dataField="role"
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: roles,
                  displayExpr: "name",
                  valueExpr: "name",
                  placeholder: t("employeeList.select"),
                  value: selectedRole,
                  onValueChanged: (e) => setSelectedRole(e.value),
                }}
                isRequired
              >
                <Label text={t("employeeList.role")} />
              </FormItem>
              <FormItem
                dataField="manager"
                editorType="dxSelectBox"
                editorOptions={{
                  showClearButton: false,
                  searchEnabled: true,
                  placeholder: t("employeeList.select"),
                  dataSource: managers,
                  displayExpr: (user) =>
                    user ? `${user.lastName} ${user.firstName}` : "",
                  valueExpr: "id",
                  value: selectedManagerId,
                  onValueChanged: (e) => {
                    setSelectedManagerId(e.value);
                    const selectedManager = managers.find(
                      (manager) => manager.id === e.value,
                    );
                    if (selectedManager) {
                      setSelectedManagerCompanyId(selectedManager.companyId);
                    }
                  },
                }}
                isRequired
              >
                <Label text={t("employeeList.manager")} />
              </FormItem>
              <FormItem
                dataField="email"
                editorType="dxTextBox"
                editorOptions={{
                  value: email,
                  onValueChanged: (e) => setEmail(e.value),
                }}
              >
                <Label text={t("employeeList.adressEmail")} />
                <RequiredRule />
                <EmailRule />
              </FormItem>
              <FormItem
                dataField="password"
                editorType="dxTextBox"
                editorOptions={{
                  value: password,
                  onValueChanged: (e) => setPassword(e.value),
                }}
                helpText={t("employeeList.passwordHelpText")}
              >
                <Label text={t("employeeList.password")} />
                <RequiredRule />
                <StringLengthRule min={3} />
              </FormItem>
            </Form>
            <ToolbarItem
              widget="dxButton"
              toolbar="bottom"
              location="before"
              options={{
                type: "normal",
                stylingMode: "outlined",
                text: t("employeeList.generatePassword"),
                onClick: () => setPassword(generatePassword()),
                disabled: isFormDisabled,
              }}
            />
            <ToolbarItem
              widget="dxButton"
              toolbar="bottom"
              location="after"
              options={{
                type: "default",
                stylingMode: "contained",
                text: t("employeeList.createAccount"),
                onClick: handleCreateAccount,
                disabled: isFormDisabled,
              }}
            />
          </Popup>
        ) : null}
        {isMobile ? (
          <div className="employees-mobile-legend">
            <div className="employees-mobile-legend-row">
              <span
                className="employees-mobile-legend-dot"
                style={{ backgroundColor: "#ff0000" }}
              />
              <span>{t("employeeList.isNotRegistred")}</span>
            </div>
            <div className="employees-mobile-legend-row">
              <span
                className="employees-mobile-legend-dot"
                style={{ backgroundColor: "#0000ff" }}
              />
              <span>{t("employeeList.registredAccount")}</span>
            </div>
            <div className="employees-mobile-legend-row">
              <span
                className="employees-mobile-legend-dot"
                style={{ backgroundColor: "#facc15" }}
              />
              <span>{t("employeeList.employeeHasDeactivatedAccount")}</span>
            </div>
            <div className="employees-mobile-legend-row">
              <span
                className="employees-mobile-legend-dot"
                style={{ backgroundColor: "#008000" }}
              />
              <span>{t("employeeList.confiermedEmailAccount")}</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#ff0000]"></div>
              <div className="leading-none">
                {t("employeeList.isNotRegistred")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#0000ff]"></div>
              <div className="leading-none">
                {t("employeeList.registredAccount")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#facc15]"></div>
              <div className="leading-none">
                {t("employeeList.employeeHasDeactivatedAccount")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#008000]"></div>
              <div className="leading-none">
                {t("employeeList.confiermedEmailAccount")}
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollView>
  );
}
